// frontend/src/pages/Checkout.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";
import PromotionExpiryWarning from "../components/Common/PromotionExpiryWarning";
import { formatPrice } from "../utils/format";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  FiTruck,
  FiCreditCard,
  FiMapPin,
  FiPhone,
  FiMail,
  FiUser,
  FiCheckCircle,
  FiLock,
  FiShield,
  FiGrid,
  FiDownload,
  FiAlertCircle,
  FiCopy,
  FiCheck,
  FiClock,
  FiInfo,
  FiGift,
  FiShoppingBag,
} from "react-icons/fi";
import { FaCcVisa } from "react-icons/fa";

// ✅ Shared gateway constant (real logos)
import { PAYMENT_GATEWAYS } from "../constants/paymentGateways";

// Map gateway -> POST target
const REDIRECT_GATEWAYS = {
  payfast: "https://www.payfast.co.za/eng/process",
};

// Fallback methods if API fails
const DEFAULT_FALLBACK_METHODS = [
  {
    gateway_id: "eft",
    name: "EFT / Direct Bank Transfer",
    is_active: true,
    is_default: false,
    GatewayInfo: PAYMENT_GATEWAYS.eft,
  },
  {
    gateway_id: "cod",
    name: "Cash on Delivery",
    is_active: true,
    is_default: false,
    GatewayInfo: PAYMENT_GATEWAYS.cod,
  },
];

const DEFAULT_BANKING_DETAILS = [
  {
    BankName: "First National Bank",
    AccountHolderName: "TooraFlex Online Store",
    AccountNumber: "6284 1234 5678",
    BranchCode: "255005",
    IsPrimary: 1,
    AccountType: "Cheque",
    LogoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/4/47/First_National_Bank_Logo.svg",
  },
];

// South African banks with universal branch codes and hosted logo URLs
const SA_BANKS = {
  "first national bank": {
    code: "250655",
    color: "#009639",
    shortName: "FNB",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/First_National_Bank_logo.svg/640px-First_National_Bank_logo.svg.png",
  },
  fnb: {
    code: "250655",
    color: "#009639",
    shortName: "FNB",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/First_National_Bank_logo.svg/640px-First_National_Bank_logo.svg.png",
  },
  absa: {
    code: "632005",
    color: "#AF1685",
    shortName: "ABSA",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Absa_Group_Limited_logo.svg/640px-Absa_Group_Limited_logo.svg.png",
  },
  "absa bank": {
    code: "632005",
    color: "#AF1685",
    shortName: "ABSA",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Absa_Group_Limited_logo.svg/640px-Absa_Group_Limited_logo.svg.png",
  },
  "standard bank": {
    code: "051001",
    color: "#0033A1",
    shortName: "Standard Bank",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/Standard_Bank_Logo.svg/640px-Standard_Bank_Logo.svg.png",
  },
  nedbank: {
    code: "198765",
    color: "#006341",
    shortName: "Nedbank",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Nedbank_logo.svg/640px-Nedbank_logo.svg.png",
  },
  capitec: {
    code: "470010",
    color: "#E30613",
    shortName: "Capitec",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Capitec_Bank_logo.svg/640px-Capitec_Bank_logo.svg.png",
  },
  "capitec bank": {
    code: "470010",
    color: "#E30613",
    shortName: "Capitec",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Capitec_Bank_logo.svg/640px-Capitec_Bank_logo.svg.png",
  },
  investec: {
    code: "580105",
    color: "#003366",
    shortName: "Investec",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Investec_logo.svg/640px-Investec_logo.svg.png",
  },
  "investec bank": {
    code: "580105",
    color: "#003366",
    shortName: "Investec",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Investec_logo.svg/640px-Investec_logo.svg.png",
  },
  "african bank": {
    code: "430000",
    color: "#FF6600",
    shortName: "African Bank",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/African_Bank_logo.svg/640px-African_Bank_logo.svg.png",
  },
  "bidvest bank": {
    code: "462005",
    color: "#005B9F",
    shortName: "Bidvest",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Bidvest_Group_logo.svg/640px-Bidvest_Group_logo.svg.png",
  },
  "discovery bank": {
    code: "679000",
    color: "#6B2C91",
    shortName: "Discovery",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2e/Discovery_Limited_Logo.svg/640px-Discovery_Limited_Logo.svg.png",
  },
  tymebank: {
    code: "678910",
    color: "#FFC107",
    shortName: "TymeBank",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/TymeBank_logo.svg/640px-TymeBank_logo.svg.png",
  },
  "bank zero": {
    code: "888000",
    color: "#000000",
    shortName: "Bank Zero",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Bank_Zero_Logo.svg/640px-Bank_Zero_Logo.svg.png",
  },
  "old mutual bank": {
    code: "460005",
    color: "#00A94F",
    shortName: "Old Mutual",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Old_Mutual_logo.svg/640px-Old_Mutual_logo.svg.png",
  },
  "grindrod bank": {
    code: "584000",
    color: "#004B87",
    shortName: "Grindrod",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Grindrod_Logo.svg/640px-Grindrod_Logo.svg.png",
  },
  "sasfin bank": {
    code: "683000",
    color: "#00539B",
    shortName: "Sasfin",
    logoUrl:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Sasfin_Logo.svg/640px-Sasfin_Logo.svg.png",
  },
};

// Get bank info helper — more forgiving lookup
const getBankInfo = (bankName = "") => {
  if (!bankName) return null;

  const normalized = bankName.toLowerCase().trim();

  // 1. Direct match
  if (SA_BANKS[normalized]) return SA_BANKS[normalized];

  // 2. Try removing common suffixes and re-matching
  const stripped = normalized
    .replace(/\s+bank\s*$/i, "") // "absa bank" → "absa"
    .replace(/\s+limited\s*$/i, "") // "absa limited" → "absa"
    .replace(/\s+ltd\.?\s*$/i, "")
    .trim();

  if (SA_BANKS[stripped]) return SA_BANKS[stripped];

  // 3. Partial match — find any key that's a prefix/substring
  const keys = Object.keys(SA_BANKS);
  const match = keys.find(
    (key) => normalized.startsWith(key) || key.startsWith(normalized),
  );
  if (match) return SA_BANKS[match];

  // 4. Fallback: match on the canonical branch code if we can extract it
  const codeMatch = normalized.match(/\b\d{6}\b/);
  if (codeMatch) {
    const byCode = Object.values(SA_BANKS).find((b) => b.code === codeMatch[0]);
    if (byCode) return byCode;
  }

  return null;
};

// Get bank logo URL directly from bank object or lookup
const getBankLogoUrl = (bank) => {
  if (!bank) return null;
  if (bank.LogoUrl) return bank.LogoUrl;
  if (bank.logo_url) return bank.logo_url;
  const info = getBankInfo(bank.BankName || bank.bankName);
  return info?.logoUrl || null;
};

// ⭐ Merge API methods with fallback defaults.
// API methods take precedence when the same gateway_id exists in both.
const mergeWithFallbackMethods = (
  apiMethods = [],
  { eftEnabled = true, codEnabled = true } = {},
) => {
  const apiIds = new Set(apiMethods.map((m) => m.gateway_id));
  const enabledFallbacks = DEFAULT_FALLBACK_METHODS.filter(
    (method) =>
      (method.gateway_id === "eft" ? eftEnabled : codEnabled) &&
      !apiIds.has(method.gateway_id),
  );
  return [
    ...apiMethods.filter(
      (method) =>
        (method.gateway_id !== "eft" || eftEnabled) &&
        (method.gateway_id !== "cod" || codEnabled),
    ),
    ...enabledFallbacks,
  ];
};

// Processing overlay
const ProcessingOverlay = ({ stage, gatewayName }) => {
  if (!stage) return null;

  const messages = {
    creating: {
      title: "Placing your order…",
      subtitle: "Please don't close this tab or go back.",
    },
    redirecting: {
      title: `Redirecting you to ${gatewayName || "payment"}…`,
      subtitle: "You'll be taken to a secure payment page in a moment.",
    },
  };

  const { title, subtitle } = messages[stage] || messages.creating;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(2px)",
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          padding: "40px 48px",
          maxWidth: "360px",
          width: "90%",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            margin: "0 auto 20px",
            border: "4px solid #e2e8f0",
            borderTopColor: "#2c7da0",
            borderRadius: "50%",
            animation: "cko-spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes cko-spin { to { transform: rotate(360deg); } }`}</style>
        <h3 style={{ margin: "0 0 8px", fontSize: "1.1rem", color: "#1e293b" }}>
          {title}
        </h3>
        <p style={{ margin: 0, fontSize: "0.9rem", color: "#64748b" }}>
          {subtitle}
        </p>
      </div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cartItems,
    cartSubtotal,
    cartTax,
    cartTaxEnabled,
    cartTaxRate,
    cartTotal,
    expiringPromotions,
    clearCart,
    loading: cartLoading,
  } = useCart();
  const { user } = useAuth();
  const [checkoutMode, setCheckoutMode] = useState(user ? "account" : null);
  const { tenant, settings: tenantSettings } = useTenant();
  const storeSettings = tenantSettings || tenant?.settings || {};
  const showDeliveryPromo =
    storeSettings.ShowDeliveryPromo ?? storeSettings.showDeliveryPromo;
  const deliveryPromoEnabled =
    showDeliveryPromo === true ||
    showDeliveryPromo === 1 ||
    ["true", "1"].includes(String(showDeliveryPromo ?? "").toLowerCase());
  const deliveryPromoThreshold = Number(
    storeSettings.DeliveryPromoThreshold ??
      storeSettings.deliveryPromoThreshold ??
      500,
  );
  const deliveryPromoEligible =
    deliveryPromoEnabled &&
    Number.isFinite(deliveryPromoThreshold) &&
    deliveryPromoThreshold > 0 &&
    Number(cartSubtotal) >= deliveryPromoThreshold;

  const [loading, setLoading] = useState(false);
  const [processingStage, setProcessingStage] = useState(null);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showEftDetails, setShowEftDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [useSavedAddress, setUseSavedAddress] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [hasLoadedCart, setHasLoadedCart] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(
    DEFAULT_FALLBACK_METHODS,
  );
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [bankingDetails, setBankingDetails] = useState(DEFAULT_BANKING_DETAILS);
  const [selectedBanking, setSelectedBanking] = useState(
    DEFAULT_BANKING_DETAILS[0],
  );
  const [loadingBanking, setLoadingBanking] = useState(true);
  const [apiError, setApiError] = useState(null);

  // ⭐ Delivery methods
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [loadingDelivery, setLoadingDelivery] = useState(true);
  const [deliveryError, setDeliveryError] = useState(null);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  // Track image load errors per gateway and bank
  const [imageErrors, setImageErrors] = useState({});

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    notes: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("payfast");

  // ⭐ Compute the selected delivery method object
  const selectedDeliveryMethod = useMemo(
    () =>
      deliveryMethods.find((m) => m.DeliveryMethodID === selectedDeliveryId) ||
      null,
    [deliveryMethods, selectedDeliveryId],
  );

  // The enabled store-wide delivery promo also overrides a method's fee.
  const shippingFee = useMemo(() => {
    if (!selectedDeliveryMethod) return 0;
    if (deliveryPromoEligible) return 0;
    return Number(selectedDeliveryMethod.Fee) || 0;
  }, [selectedDeliveryMethod, deliveryPromoEligible]);

  const getDeliveryMethodFee = (method) =>
    deliveryPromoEligible ? 0 : Number(method.Fee ?? method.BaseFee) || 0;

  // ⭐ Grand total = cartTotal + shippingFee
  const grandTotal = useMemo(
    () => (Number(cartTotal) || 0) + shippingFee,
    [cartTotal, shippingFee],
  );

  // ⭐ Get current bank info for selected banking
  const currentBankInfo = useMemo(() => {
    if (!selectedBanking) return null;
    return getBankInfo(selectedBanking.BankName);
  }, [selectedBanking]);

  // Image error handler
  const handleImageError = (key) => {
    setImageErrors((prev) => ({ ...prev, [key]: true }));
  };

  const handleCopyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`${fieldName} copied!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // ============================================
  // FETCH PAYMENT METHODS
  // ⭐ Always merges with fallbacks so EFT/COD never disappear
  // ============================================
  const fetchPaymentMethods = async () => {
    setLoadingMethods(true);
    setApiError(null);

    const normalize = (raw) =>
      raw.map((method) => ({
        gateway_id: method.GatewayID,
        name: method.Name || method.GatewayInfo?.name || method.GatewayID,
        is_active: true,
        is_default: method.IsDefault === true || method.IsDefault === 1,
        config: method.Config || {},
        GatewayInfo: method.GatewayInfo ||
          PAYMENT_GATEWAYS[method.GatewayID] || {
            id: method.GatewayID,
            name: method.Name || method.GatewayID,
            description: "Online Payment Gateway",
            type: "redirect",
          },
      }));

    const applyMethods = (methods, defaultPreference, checkoutOptions) => {
      const merged = mergeWithFallbackMethods(methods, checkoutOptions);
      setPaymentMethods(merged);

      const defaultMethod =
        merged.find((m) => m.is_default === true) ||
        merged.find((m) => m.gateway_id === defaultPreference) ||
        merged[0];

      setPaymentMethod(defaultMethod?.gateway_id || "");
    };

    let checkoutOptions = {};
    try {
      const response = await api.get("/api/public/payment-methods/active");
      checkoutOptions = response?.data?.checkoutOptions || {};

      if (response?.data?.success && response.data.paymentMethods?.length > 0) {
        applyMethods(
          normalize(response.data.paymentMethods),
          "eft",
          checkoutOptions,
        );
        return;
      }

      const fallbackResponse = await api.get("/api/payment-methods/active");
      checkoutOptions =
        fallbackResponse?.data?.checkoutOptions || checkoutOptions;

      if (
        fallbackResponse?.data?.success &&
        fallbackResponse.data.paymentMethods?.length > 0
      ) {
        applyMethods(
          normalize(fallbackResponse.data.paymentMethods),
          "eft",
          checkoutOptions,
        );
        return;
      }

      const merged = mergeWithFallbackMethods([], checkoutOptions);
      setPaymentMethods(merged);
      setPaymentMethod(merged[0]?.gateway_id || "");
    } catch (error) {
      console.warn("⚠️ Fetching payment methods failed:", error.message);

      const merged = mergeWithFallbackMethods([], checkoutOptions);
      setPaymentMethods(merged);
      setPaymentMethod(merged[0]?.gateway_id || "");

      if (error.response?.status === 404) {
        setApiError(
          "Payment methods endpoint not found. Using default payment options.",
        );
      }
    } finally {
      setLoadingMethods(false);
    }
  };

  // ============================================
  // FETCH BANKING DETAILS
  // ============================================
  const fetchBankingDetails = async () => {
    setLoadingBanking(true);
    try {
      const response = await api.get("/api/public/banking-details");

      if (response?.data?.success && response.data.bankingDetails?.length > 0) {
        setBankingDetails(response.data.bankingDetails);
        const primaryBank = response.data.bankingDetails.find(
          (bank) => bank.IsPrimary === 1,
        );
        setSelectedBanking(
          primaryBank || response.data.bankingDetails[0] || null,
        );
        return;
      }

      const fallbackResponse = await api.get("/api/banking-details");

      if (
        fallbackResponse?.data?.success &&
        fallbackResponse.data.bankingDetails?.length > 0
      ) {
        setBankingDetails(fallbackResponse.data.bankingDetails);
        const primaryBank = fallbackResponse.data.bankingDetails.find(
          (bank) => bank.IsPrimary === 1,
        );
        setSelectedBanking(
          primaryBank || fallbackResponse.data.bankingDetails[0] || null,
        );
        return;
      }

      setBankingDetails(DEFAULT_BANKING_DETAILS);
      setSelectedBanking(DEFAULT_BANKING_DETAILS[0]);
    } catch (error) {
      console.error("❌ Error fetching banking details:", error.message);
      setBankingDetails(DEFAULT_BANKING_DETAILS);
      setSelectedBanking(DEFAULT_BANKING_DETAILS[0]);
    } finally {
      setLoadingBanking(false);
    }
  };

  // ============================================
  // ⭐ FETCH DELIVERY METHODS
  // ============================================
  const fetchDeliveryMethods = async (postalCode) => {
    setLoadingDelivery(true);
    setDeliveryError(null);
    try {
      const params = {};
      if (postalCode) params.postalCode = postalCode;
      if (cartSubtotal) params.orderTotal = cartSubtotal;

      const response = await api.get("/api/public/delivery-methods/active", {
        params,
      });

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Could not load delivery methods.",
        );
      }

      const methods = response.data.deliveryMethods || [];
      setDeliveryMethods(methods);
      setSelectedDeliveryId((previousId) => {
        if (previousId && methods.some((m) => m.DeliveryMethodID === previousId)) {
          return previousId;
        }
        const defaultMethod = methods.find((method) => method.IsDefault);
        return (defaultMethod || methods[0])?.DeliveryMethodID || null;
      });
      console.log("✅ Delivery methods loaded:", methods.length);
    } catch (error) {
      console.error("Error fetching delivery methods:", error);
      setDeliveryMethods([]);
      setSelectedDeliveryId(null);
      setDeliveryError(
        error.response?.data?.message ||
          "Unable to load this store's delivery options. Try again.",
      );
    } finally {
      setLoadingDelivery(false);
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    if (method === "eft") {
      setShowEftDetails(true);
      setShowPayModal(true);
    } else {
      setShowEftDetails(false);
      setShowPayModal(false);
    }
  };

  const handleDeliveryMethodChange = (methodId) => {
    setSelectedDeliveryId(methodId);
  };

  const loadSavedAddresses = async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.data && response.data.addressLine1) {
        setAddresses([
          {
            id: "primary",
            name: "Primary Address",
            addressLine1: response.data.addressLine1,
            addressLine2: response.data.addressLine2,
            city: response.data.city,
            postalCode: response.data.postalCode,
            isDefault: true,
          },
        ]);
        setSelectedAddressId("primary");
      }
    } catch (error) {
      console.error("Error loading addresses:", error);
    }
  };

  // ============================================
  // PLACE ORDER
  // ============================================
  const placeOrderAfterPayment = async () => {
    if (!selectedDeliveryMethod) {
      toast.error("Select an available delivery method before placing your order.");
      return;
    }

    setLoading(true);
    setProcessingStage("creating");
    try {
      let shippingAddress;
      if (useSavedAddress && selectedAddressId) {
        const selectedAddress = addresses.find(
          (a) => a.id === selectedAddressId,
        );
        if (selectedAddress) {
          shippingAddress = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            address:
              `${selectedAddress.addressLine1} ${selectedAddress.addressLine2 || ""}`.trim(),
            city: selectedAddress.city,
            postalCode: selectedAddress.postalCode,
            phone: formData.phone,
            email: formData.email,
          };
        }
      } else {
        shippingAddress = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          phone: formData.phone,
          email: formData.email,
        };
      }

      const formattedItems = cartItems.map((item) => ({
        productId: item.ProductID,
        quantity: item.Quantity,
        price: item.UnitPrice || item.Price || item.SellingPrice,
      }));

      const selectedMethod = paymentMethods.find(
        (m) => m.gateway_id === paymentMethod,
      );
      const isGateway =
        selectedMethod &&
        selectedMethod.gateway_id !== "cod" &&
        selectedMethod.gateway_id !== "eft" &&
        selectedMethod.gateway_id !== "card";

      const orderData = {
        items: formattedItems,
        cartItems: formattedItems,
        subtotal: cartSubtotal || 0,
        taxAmount: cartTax || 0,
        // ⭐ Shipping info
        shippingFee: shippingFee,
        shippingMethodId: selectedDeliveryMethod?.DeliveryMethodID || null,
        shippingMethodCode: selectedDeliveryMethod?.Code || null,
        shippingMethodName: selectedDeliveryMethod?.Name || null,
        totalAmount: grandTotal || 0,
        shippingAddress,
        paymentMethod: isGateway ? "online" : paymentMethod,
        paymentGateway: isGateway ? paymentMethod : null,
        notes: formData.notes,
      };

      console.log("🛒 Posting order:", {
        paymentMethod: orderData.paymentMethod,
        paymentGateway: orderData.paymentGateway,
        shippingFee: orderData.shippingFee,
        shippingMethodCode: orderData.shippingMethodCode,
        itemCount: formattedItems.length,
        totalAmount: orderData.totalAmount,
      });

      const response = await api.post("/api/orders", orderData);

      if (response.data.success) {
        const newOrderId = response.data.orderNumber || response.data.orderId;
        setOrderId(newOrderId);

        const gatewayName =
          PAYMENT_GATEWAYS[paymentMethod]?.name || paymentMethod;

        if (response.data.paymentUrl && !response.data.payfastData) {
          setProcessingStage("redirecting");
          toast.loading(`Redirecting to ${gatewayName}…`);
          await new Promise((resolve) => setTimeout(resolve, 250));
          window.location.href = response.data.paymentUrl;
          return;
        }

        const gatewayRedirectPayload = response.data.payfastData || null;
        const gatewayUrl =
          response.data.paymentUrl || REDIRECT_GATEWAYS[paymentMethod] || null;

        if (gatewayRedirectPayload && gatewayUrl) {
          setProcessingStage("redirecting");
          toast.loading(`Redirecting to ${gatewayName}…`);
          await new Promise((resolve) => setTimeout(resolve, 250));

          const form = document.createElement("form");
          form.method = "POST";
          form.action = gatewayUrl;

          Object.keys(gatewayRedirectPayload).forEach((key) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = gatewayRedirectPayload[key];
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          return;
        }

        clearCart();
        toast.success("✅ Order placed successfully!");
        if (user?.role === "Admin" || !user) {
          setOrderPlaced(true);
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error("Order error:", error);
      toast.error(error.response?.data?.message || "Failed to place order");
    } finally {
      setLoading(false);
      setProcessingStage(null);
    }
  };

  // ============================================
  // PAYMENT RETURN HANDLING
  // ============================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentSuccess = params.get("payment_success");
    const paymentPending = params.get("payment_pending");
    const orderIdFromUrl = params.get("orderId");
    const paymentCancelled = params.get("paymentCancelled");
    const paymentError = params.get("payment_error");

    if (paymentSuccess === "1" && orderIdFromUrl) {
      clearCart();
      toast.success("🎉 Payment successful! Your order has been confirmed.");
      if (user?.role === "Admin" || !user) {
        setOrderId(orderIdFromUrl);
        setOrderPlaced(true);
      } else {
        navigate("/dashboard");
      }
      window.history.replaceState({}, "", "/checkout");
      return;
    }

    if (paymentPending === "1" && orderIdFromUrl) {
      toast("Payment is awaiting confirmation. We'll update your order shortly.");
      window.history.replaceState({}, "", "/checkout");
      return;
    }

    if (paymentCancelled === "1" && orderIdFromUrl) {
      toast.error("❌ Payment was cancelled. You can try again.");
      window.history.replaceState({}, "", "/checkout");
      return;
    }

    if (paymentError === "1" && orderIdFromUrl) {
      toast.error(
        "❌ There was a problem with your payment. Please try again.",
      );
      window.history.replaceState({}, "", "/checkout");
      return;
    }
  }, [location.search, clearCart, navigate, user]);

  useEffect(() => {
    if (!cartLoading && !hasLoadedCart) {
      setHasLoadedCart(true);
    }
  }, [cartLoading, hasLoadedCart]);

  useEffect(() => {
    if (user) setCheckoutMode("account");
  }, [user]);

  useEffect(() => {
    fetchPaymentMethods();
    fetchBankingDetails();
    fetchDeliveryMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⭐ Re-fetch delivery methods when postal code or subtotal changes so free-shipping recalculates
  useEffect(() => {
    const postal =
      (useSavedAddress && selectedAddressId
        ? addresses.find((a) => a.id === selectedAddressId)?.postalCode
        : formData.postalCode) || null;

    fetchDeliveryMethods(postal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.postalCode, selectedAddressId, useSavedAddress, cartSubtotal]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName || user.name?.split(" ")[0] || "",
        lastName: user.lastName || user.name?.split(" ")[1] || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
      }));
      loadSavedAddresses();
    }
  }, [user]);

  // ============================================
  // CART-EMPTY REDIRECT
  // ============================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isPaymentReturn =
      params.get("payment_success") ||
      params.get("payment_pending") ||
      params.get("paymentCancelled") ||
      params.get("payment_error");

    if (isPaymentReturn) return;

    if (
      !cartLoading &&
      hasLoadedCart &&
      cartItems.length === 0 &&
      !orderPlaced &&
      !loading
    ) {
      const t = setTimeout(() => {
        navigate("/shop", { replace: true });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [
    cartItems.length,
    cartLoading,
    navigate,
    orderPlaced,
    loading,
    hasLoadedCart,
    location.search,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const required = ["firstName", "lastName", "email", "phone"];
    if (!useSavedAddress || addresses.length === 0) {
      required.push("address", "city", "postalCode");
    }
    for (const field of required) {
      if (!formData[field]) {
        toast.error(
          `Please fill in ${field.replace(/([A-Z])/g, " $1").toLowerCase()}`,
        );
        return false;
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return false;
    }
    // ⭐ Require a delivery method
    if (!selectedDeliveryMethod) {
      toast.error("Please select a delivery method");
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (paymentMethod === "eft") {
      setShowEftDetails(true);
      setShowPayModal(true);
    } else {
      await placeOrderAfterPayment();
    }
  };

  const handleProceedFromModal = () => {
    setShowPayModal(false);
    setShowEftDetails(false);
    placeOrderAfterPayment();
  };

  // ============================================
  // Render bank logo with fallback badge
  // ============================================
  const renderBankLogo = (bank) => {
    if (!bank) return <FiDownload size={24} />;

    const logoUrl = getBankLogoUrl(bank);
    const bankKey = `bank-${bank.BankName || bank.bankName || "default"}`;
    const info = getBankInfo(bank.BankName || bank.bankName);

    if (logoUrl && !imageErrors[bankKey]) {
      return (
        <img
          src={logoUrl}
          alt={bank.BankName || "Bank Logo"}
          style={{
            maxHeight: "36px",
            maxWidth: "100px",
            objectFit: "contain",
          }}
          onError={() => handleImageError(bankKey)}
        />
      );
    }

    if (info) {
      return (
        <span
          className="cko-bank-logo-badge"
          style={{
            background: info.color,
            color: "#ffffff",
            padding: "6px 12px",
            borderRadius: "6px",
            fontWeight: "700",
            fontSize: "0.85rem",
            letterSpacing: "0.5px",
          }}
        >
          {info.shortName}
        </span>
      );
    }

    return <FiDownload size={24} />;
  };

  // ============================================
  // Render real gateway logos
  // ============================================
  const renderPaymentIcon = (gatewayId, apiIconUrl) => {
    const meta = PAYMENT_GATEWAYS[gatewayId];
    const iconUrl = meta?.icon || apiIconUrl;

    if (iconUrl && !imageErrors[gatewayId]) {
      return (
        <img
          src={iconUrl}
          alt={meta?.name || gatewayId}
          className="cko-gateway-logo"
          loading="lazy"
          onError={() => handleImageError(gatewayId)}
        />
      );
    }

    const fallbackKey = `${gatewayId}-fb`;
    if (meta?.fallbackIcon && !imageErrors[fallbackKey]) {
      return (
        <img
          src={meta.fallbackIcon}
          alt={meta?.name || gatewayId}
          className="cko-gateway-logo"
          loading="lazy"
          onError={() => handleImageError(fallbackKey)}
        />
      );
    }

    const iconMap = {
      card: <FaCcVisa />,
      eft: <FiDownload />,
      cod: <FiTruck />,
      scan: <FiGrid />,
    };
    return iconMap[gatewayId] || <FiCreditCard />;
  };

  // ⭐ Render delivery icon — uses provider logo if available, else truck icon
  const renderDeliveryIcon = (method) => {
    if (method.ProviderLogo && !imageErrors[method.DeliveryMethodID]) {
      return (
        <img
          src={method.ProviderLogo}
          alt={method.ProviderName || method.Name}
          className="cko-gateway-logo"
          loading="lazy"
          onError={() => handleImageError(method.DeliveryMethodID)}
        />
      );
    }
    return <FiTruck />;
  };

  if (cartLoading && !hasLoadedCart) {
    return (
      <div className="cko-loading">
        <div className="cko-spinner"></div>
        <p>Loading checkout...</p>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="cko-confirmation">
        <div className="cko-confirmation-card">
          <div className="cko-confirmation-icon">
            <FiCheckCircle />
          </div>
          <h1>Order Received! 🎉</h1>
          <p>Your order number is:</p>
          <div className="cko-order-number">{orderId}</div>
          <p>
            Order updates will be sent to{" "}
            <strong>{formData.email}</strong>.
          </p>
          {paymentMethod === "eft" && selectedBanking && (
            <div className="cko-eft-success-info">
              <h4>Banking Details for Payment:</h4>
              <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <strong>Bank:</strong> {renderBankLogo(selectedBanking)}{" "}
                <span>{selectedBanking.BankName}</span>
              </p>
              <p>
                <strong>Account Holder:</strong>{" "}
                {selectedBanking.AccountHolderName}
              </p>
              <p>
                <strong>Account Number:</strong> {selectedBanking.AccountNumber}
              </p>
              <p>
                <strong>Amount:</strong> {formatPrice(grandTotal)}
              </p>
              <p>
                <strong>Reference:</strong> {orderId}
              </p>
              <p className="cko-eft-note">
                Please complete your EFT payment using these details.
              </p>
            </div>
          )}
          <div className="cko-confirmation-buttons">
            <button
              onClick={() => navigate("/shop")}
              className="cko-btn-continue"
            >
              Continue Shopping
            </button>
            {user && (
              <button
                onClick={() => {
                  if (user?.role === "Admin") {
                    navigate("/admin/orders");
                  } else {
                    navigate("/orders");
                  }
                }}
                className="cko-btn-orders"
              >
                View My Orders
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!user && checkoutMode === null && cartItems.length > 0) {
    const accountRouteState = { from: { pathname: "/checkout" } };
    return (
      <div className="cko-page">
        <div className="cko-container cko-checkout-choice">
          <h1>Choose how to checkout</h1>
          <p>You can create an account or place your order as a guest.</p>
          <div className="cko-choice-options">
            <button
              type="button"
              className="cko-choice-card"
              onClick={() => navigate("/register", { state: accountRouteState })}
            >
              <FiUser aria-hidden="true" />
              <strong>Create an account</strong>
              <span>Save your details and track your orders.</span>
            </button>
            <button
              type="button"
              className="cko-choice-card"
              onClick={() => setCheckoutMode("guest")}
            >
              <FiShoppingBag aria-hidden="true" />
              <strong>Continue as guest</strong>
              <span>Checkout without creating an account.</span>
            </button>
          </div>
          <button
            type="button"
            className="cko-choice-login"
            onClick={() => navigate("/login", { state: accountRouteState })}
          >
            Already have an account? Sign in
          </button>
          <button
            type="button"
            className="cko-choice-back"
            onClick={() => navigate("/cart")}
          >
            Back to Cart
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ProcessingOverlay
        stage={processingStage}
        gatewayName={PAYMENT_GATEWAYS[paymentMethod]?.name}
      />

      <div className="cko-page">
        <div className="cko-container">
          <h1>Checkout</h1>

          {expiringPromotions.length > 0 && (
            <PromotionExpiryWarning items={cartItems} />
          )}

          {!user && checkoutMode === "guest" && (
            <div className="cko-guest-notice">
              Checking out as a guest.{" "}
              <button
                type="button"
                onClick={() =>
                  navigate("/register", {
                    state: { from: { pathname: "/checkout" } },
                  })
                }
              >
                Create an account instead
              </button>
            </div>
          )}

          {apiError && (
            <div className="cko-api-warning">
              <FiAlertCircle />
              <span>{apiError}</span>
            </div>
          )}

          <div className="cko-grid">
            <div className="cko-form-container">
              <form onSubmit={handlePlaceOrder} className="cko-form">
                {/* Personal info */}
                <div className="cko-section">
                  <h3>
                    <FiUser /> Personal Information
                  </h3>
                  <div className="cko-row">
                    <div className="cko-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="cko-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="cko-row">
                    <div className="cko-group">
                      <label>
                        <FiMail /> Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="cko-group">
                      <label>
                        <FiPhone /> Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+27 XX XXX XXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery address */}
                <div className="cko-section">
                  <h3>
                    <FiMapPin /> Delivery Address
                  </h3>
                  {addresses.length > 0 && (
                    <div className="cko-address-toggle">
                      <label>
                        <input
                          type="radio"
                          checked={useSavedAddress}
                          onChange={() => setUseSavedAddress(true)}
                        />{" "}
                        Saved address
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={!useSavedAddress}
                          onChange={() => setUseSavedAddress(false)}
                        />{" "}
                        New address
                      </label>
                    </div>
                  )}
                  {useSavedAddress && addresses.length > 0 ? (
                    <div className="cko-saved-addresses">
                      {addresses.map((addr) => (
                        <label key={addr.id} className="cko-address-option">
                          <input
                            type="radio"
                            name="addressId"
                            value={addr.id}
                            checked={selectedAddressId === addr.id}
                            onChange={() => setSelectedAddressId(addr.id)}
                          />
                          <div className="cko-address-details">
                            <strong>{addr.name}</strong>
                            <p>
                              {addr.addressLine1} {addr.addressLine2 || ""}
                            </p>
                            <p>
                              {addr.city}, {addr.postalCode}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="cko-new-address">
                      <div className="cko-group">
                        <label>Street Address *</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          required={!useSavedAddress}
                        />
                      </div>
                      <div className="cko-row">
                        <div className="cko-group">
                          <label>City *</label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            required={!useSavedAddress}
                          />
                        </div>
                        <div className="cko-group">
                          <label>Postal Code *</label>
                          <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            required={!useSavedAddress}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ⭐ Delivery method section */}
                <div className="cko-section">
                  <h3>
                    <FiTruck /> Delivery Method
                  </h3>
                  {deliveryPromoEligible && (
                    <div
                      className="cko-delivery-promo-success"
                      role="status"
                      aria-live="polite"
                    >
                      <FiGift aria-hidden="true" />
                      <div>
                        <strong>You’ve unlocked free delivery!</strong>
                        <span>
                          Your product subtotal reached{" "}
                          {formatPrice(deliveryPromoThreshold)}. Enjoy the
                          celebration — delivery is on us!
                        </span>
                      </div>
                    </div>
                  )}
                  {loadingDelivery ? (
                    <div className="cko-loading-methods">
                      <div className="cko-spinner-small"></div>
                      <span>Loading delivery options...</span>
                    </div>
                  ) : deliveryMethods.length === 0 ? (
                    <div className="cko-no-methods">
                      <FiAlertCircle />
                      <p>
                        {deliveryError ||
                          "No delivery options are currently configured for this address. Please contact the store."}
                      </p>
                      {deliveryError && (
                        <button
                          type="button"
                          onClick={() => {
                            const postalCode =
                              (useSavedAddress && selectedAddressId
                                ? addresses.find(
                                    (address) => address.id === selectedAddressId,
                                  )?.postalCode
                                : formData.postalCode) || null;
                            fetchDeliveryMethods(postalCode);
                          }}
                        >
                          Try again
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="cko-payment-methods">
                      {deliveryMethods.map((method) => {
                        const isSelected =
                          selectedDeliveryId === method.DeliveryMethodID;
                        return (
                          <label
                            key={method.DeliveryMethodID}
                            className="cko-payment-option"
                          >
                            <input
                              type="radio"
                              name="deliveryMethod"
                              value={method.DeliveryMethodID}
                              checked={isSelected}
                              onChange={() =>
                                handleDeliveryMethodChange(
                                  method.DeliveryMethodID,
                                )
                              }
                            />
                            <div className="cko-payment-content">
                              <div className="cko-payment-icon">
                                {renderDeliveryIcon(method)}
                              </div>
                              <div style={{ flex: 1 }}>
                                <strong>{method.Name}</strong>
                                <span>
                                  {method.Description ||
                                    method.ProviderName ||
                                    "Delivery option"}
                                </span>
                                {method.EstimatedDays && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      marginTop: 4,
                                      fontSize: "0.8rem",
                                      color: "#64748b",
                                    }}
                                  >
                                    <FiClock size={12} /> {method.EstimatedDays}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  marginLeft: "auto",
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color:
                                    method.IsFree ||
                                    getDeliveryMethodFee(method) === 0
                                      ? "#059669"
                                      : "#0f172a",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {method.IsFree ||
                                getDeliveryMethodFee(method) === 0 ? (
                                  <>FREE</>
                                ) : (
                                  <>{formatPrice(getDeliveryMethodFee(method))}</>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Payment method */}
                <div className="cko-section">
                  <h3>
                    <FiCreditCard /> Payment Method
                  </h3>
                  {loadingMethods ? (
                    <div className="cko-loading-methods">
                      <div className="cko-spinner-small"></div>
                      <span>Loading payment options...</span>
                    </div>
                  ) : paymentMethods.length === 0 ? (
                    <div className="cko-no-methods">
                      <FiAlertCircle />
                      <p>No payment options found. Please try refreshing.</p>
                    </div>
                  ) : (
                    <div className="cko-payment-methods">
                      {paymentMethods.map((method) => (
                        <label
                          key={method.gateway_id}
                          className="cko-payment-option"
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.gateway_id}
                            checked={paymentMethod === method.gateway_id}
                            onChange={() =>
                              handlePaymentMethodChange(method.gateway_id)
                            }
                          />
                          <div className="cko-payment-content">
                            <div className="cko-payment-icon">
                              {renderPaymentIcon(
                                method.gateway_id,
                                method.GatewayInfo?.icon,
                              )}
                            </div>
                            <div>
                              <strong>
                                {method.name || method.gateway_id}
                              </strong>
                              <span>
                                {method.GatewayInfo?.description ||
                                  `Pay using ${method.name}`}
                              </span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="cko-section cko-notes-section">
                  <h3>Order Notes (Optional)</h3>
                  <div className="cko-group">
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Special instructions or delivery notes"
                      rows="2"
                    />
                  </div>
                </div>

                <div className="cko-actions">
                  <button
                    type="button"
                    className="cko-btn-back"
                    onClick={() => navigate("/cart")}
                  >
                    Back to Cart
                  </button>
                  <button
                    type="submit"
                    className="cko-btn-place"
                    disabled={
                      loading ||
                      loadingMethods ||
                      loadingDelivery ||
                      deliveryMethods.length === 0 ||
                      !selectedDeliveryMethod ||
                      !paymentMethod
                    }
                  >
                    {loading
                      ? "Processing..."
                      : `Place Order • ${formatPrice(grandTotal)}`}
                  </button>
                </div>

                <div className="cko-secure-footer">
                  <FiLock /> <FiShield />
                  <span>Secure 256-Bit SSL Encrypted Checkout</span>
                </div>
              </form>
            </div>

            {/* Order summary */}
            <div className="cko-summary">
              <div className="cko-summary-header">
                <h2>Order Summary</h2>
                <span className="cko-item-count">{cartItems.length} items</span>
              </div>
              <div className="cko-summary-items">
                {cartItems.map((item, idx) => (
                  <div key={idx} className="cko-summary-item">
                    <div className="cko-item-image">
                      <img
                        src={item.ImageUrl || "/images/placeholder.jpg"}
                        alt={item.Name}
                      />
                    </div>
                    <div className="cko-item-details">
                      <h4>{item.Name}</h4>
                      <div className="cko-item-meta">
                        <span>Qty: {item.Quantity}</span>
                        {item.SKU && <span>• SKU: {item.SKU}</span>}
                      </div>
                    </div>
                    <div className="cko-item-price">
                      {formatPrice(
                        (item.UnitPrice || item.Price) * item.Quantity,
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="cko-summary-totals">
                <div className="cko-summary-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(cartSubtotal)}</span>
                </div>
                {cartTaxEnabled && (
                  <div className="cko-summary-row">
                    <span>VAT ({cartTaxRate}%)</span>
                    <span>{formatPrice(cartTax)}</span>
                  </div>
                )}
                {/* ⭐ Shipping row */}
                <div className="cko-summary-row">
                  <span>Shipping</span>
                  <span>
                    {selectedDeliveryMethod ? (
                      selectedDeliveryMethod.IsFree || shippingFee === 0 ? (
                        <span style={{ color: "#059669", fontWeight: 600 }}>
                          FREE
                        </span>
                      ) : (
                        formatPrice(shippingFee)
                      )
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </span>
                </div>
                <div className="cko-summary-divider"></div>
                <div className="cko-summary-row total">
                  <span>Total</span>
                  <span>{formatPrice(grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EFT modal - Updated with South African banking details & bank logos */}
      {showPayModal && paymentMethod === "eft" && showEftDetails && (
        <div
          className="cko-modal-overlay"
          onClick={() => setShowPayModal(false)}
        >
          <div
            className="cko-modal cko-eft-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="cko-modal-close"
              onClick={() => setShowPayModal(false)}
            >
              &times;
            </button>
            <div className="cko-eft-payment">
              <div className="cko-eft-header">
                <div className="cko-eft-bank-badge">
                  {renderBankLogo(selectedBanking)}
                </div>
                <div>
                  <h3>EFT / Bank Transfer</h3>
                  <p className="cko-eft-subtitle">
                    Complete payment using the details below
                  </p>
                </div>
              </div>

              {loadingBanking ? (
                <div className="cko-loading-banking">
                  <div className="cko-spinner-small"></div>
                  <p>Loading banking details...</p>
                </div>
              ) : selectedBanking ? (
                <>
                  <div className="cko-bank-details-display">
                    {/* Bank name row with bank logo */}
                    <div className="cko-bank-row cko-bank-primary">
                      <strong>Bank:</strong>
                      <span
                        className="cko-bank-name"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {renderBankLogo(selectedBanking)}
                        {selectedBanking.BankName}
                      </span>
                    </div>

                    <div className="cko-bank-row">
                      <strong>Account Holder:</strong>
                      <span>{selectedBanking.AccountHolderName}</span>
                    </div>

                    <div className="cko-bank-row cko-bank-highlight">
                      <strong>Account Number:</strong>
                      <span className="cko-account-number">
                        {selectedBanking.AccountNumber}
                      </span>
                      <button
                        className="cko-copy-btn"
                        onClick={() =>
                          handleCopyToClipboard(
                            selectedBanking.AccountNumber,
                            "Account Number",
                          )
                        }
                        title="Copy account number"
                      >
                        {copiedField === "Account Number" ? (
                          <FiCheck />
                        ) : (
                          <FiCopy />
                        )}
                      </button>
                    </div>

                    <div className="cko-bank-row">
                      <strong>Branch Code:</strong>
                      <span>
                        {selectedBanking.BranchCode ||
                          currentBankInfo?.code ||
                          "—"}
                      </span>
                      {(selectedBanking.BranchCode ||
                        currentBankInfo?.code) && (
                        <button
                          className="cko-copy-btn"
                          onClick={() =>
                            handleCopyToClipboard(
                              selectedBanking.BranchCode ||
                                currentBankInfo?.code,
                              "Branch Code",
                            )
                          }
                          title="Copy branch code"
                        >
                          {copiedField === "Branch Code" ? (
                            <FiCheck />
                          ) : (
                            <FiCopy />
                          )}
                        </button>
                      )}
                    </div>

                    {selectedBanking.AccountType && (
                      <div className="cko-bank-row">
                        <strong>Account Type:</strong>
                        <span>{selectedBanking.AccountType}</span>
                      </div>
                    )}

                    <div className="cko-bank-row cko-amount-row">
                      <strong>Amount:</strong>
                      <span className="cko-amount">
                        {formatPrice(grandTotal)}
                      </span>
                    </div>

                    <div className="cko-bank-row cko-reference-row">
                      <strong>Reference:</strong>
                      <span className="cko-reference">
                        ORDER-{Date.now().toString().slice(-8)}
                      </span>
                      <button
                        className="cko-copy-btn"
                        onClick={() =>
                          handleCopyToClipboard(
                            `ORDER-${Date.now().toString().slice(-8)}`,
                            "Reference",
                          )
                        }
                        title="Copy reference"
                      >
                        {copiedField === "Reference" ? <FiCheck /> : <FiCopy />}
                      </button>
                    </div>
                  </div>

                  <div className="cko-eft-note">
                    <FiInfo size={16} />
                    <span>
                      Please use your order number as the payment reference.
                      Payments may take 1-2 business days to reflect.
                    </span>
                  </div>

                  <button
                    onClick={handleProceedFromModal}
                    className="cko-proceed-btn"
                  >
                    I Have Made the Payment
                  </button>
                </>
              ) : (
                <p>Banking details currently unavailable.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Checkout;
